import type {
  P2PChainName,
  P2POrderSide,
  P2PFillMode,
  P2POrderStatus,
  P2PTradeStatus,
} from './p2p/tokens';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://w2usdt.com/api/v1'

// ============================================================
// P2P TYPES
// ============================================================

export type {
  P2PChainName,
  P2POrderSide,
  P2PFillMode,
  P2POrderStatus,
  P2PTradeStatus,
};

export interface P2POrder {
  id: string;
  orderId: string;
  chain: P2PChainName;
  maker: string;
  side: P2POrderSide;
  amount: string;
  expectedAmount: string;
  filled: string;
  premiumBps: number;
  fillMode: P2PFillMode;
  expiresAt: string;
  status: P2POrderStatus;
  isResidual: boolean;
  isTake: boolean;
  createdTxHash: string;
  closedTxHash: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface P2PTrade {
  id: string;
  tradeId: string;
  takerOrderId: string;
  makerOrderId: string;
  takerAddress: string;
  makerAddress: string;
  amountW2usdt: string;
  amountUsdt: string;
  premiumBpsApplied: number;
  feeAmountTaker: string;
  feeAmountMaker: string;
  uni2TxHash: string | null;
  bscTxHash: string | null;
  status: P2PTradeStatus;
  createdAt: string;
  settledAt: string | null;
}

export interface P2POrderbookSnapshot {
  pair: string;
  orders: P2POrder[];
  timestamp: string;
}

export interface P2PDepthLevel {
  premiumBps: number;
  totalAmount: string;
  orderCount: number;
}

export interface P2PDepth {
  pair: string;
  sellW2usdt: P2PDepthLevel[];
  sellUsdt: P2PDepthLevel[];
}

export interface P2PStats {
  pair: string;
  period: string;
  tradesCount: number;
  volumeW2usdt: string;
  volumeUsdt: string;
}

export interface P2PCalldata {
  chain: P2PChainName;
  to: `0x${string}`;
  data: `0x${string}`;
  value: '0';
}

export interface P2PRelayerBalanceSnapshot {
  chain: P2PChainName;
  address: `0x${string}`;
  balanceWei: string;
  balanceEther: string;
  thresholdWei: string;
  thresholdEther: string;
  low: boolean;
  empty: boolean;
  checkedAt: number;
}

export interface P2PRelayerBalanceResponse {
  relayers: P2PRelayerBalanceSnapshot[];
  summary: { anyEmpty: boolean; anyLow: boolean; healthy: boolean };
}

export interface CreateP2POrderDto {
  side: P2POrderSide;
  amount: string;
  premiumBps: number;
  expiresAt: number;
  fillMode: P2PFillMode;
  /** When set, this is a directed "take" against the given maker order.
   *  Backend overrides expiresAt/fillMode and only matches the target. */
  targetMakerOrderId?: string;
  /** Required when targetMakerOrderId is set. Address that will broadcast
   *  the createOrder tx (used by the listener to identify the take). */
  takerAddress?: string;
}

export interface CrossExistingOk {
  jobId: string;
  premiumAppliedBps: number;
}

export interface CrossExistingConfirm {
  requiresConfirmation: true;
  myPremiumBps: number;
  targetPremiumBps: number;
  deltaBps: number;
  thresholdBps: number;
}

export type CrossExistingResponse = CrossExistingOk | CrossExistingConfirm;

class ApiClient {
  private token: string | null = null

  constructor() {
    // Load token from localStorage on init
    if (typeof window !== 'undefined') {
      this.token = localStorage.getItem('bridge_auth_token')
    }
  }

  setToken(token: string | null) {
    this.token = token
    if (token) {
      localStorage.setItem('bridge_auth_token', token)
    } else {
      localStorage.removeItem('bridge_auth_token')
    }
  }

  getToken() {
    return this.token
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    if (this.token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${this.token}`
    }

    console.log(`API Request: ${options.method || 'GET'} ${url}`, {
      hasToken: !!this.token,
      body: options.body
    })

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      console.log(`API Response: ${response.status} ${response.statusText}`)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Error de conexión' }))
        console.error('API Error:', error)
        throw new Error(error.message || `Error ${response.status}`)
      }

      const data = await response.json()
      console.log('API Response data:', data)
      return data
    } catch (error) {
      console.error('API Request failed:', error)
      throw error
    }
  }

  // Auth endpoints
  async getNonce(address: string): Promise<{ nonce: string }> {
    return this.request(`/auth/nonce?address=${address}`)
  }

  async checkIsAdmin(address: string): Promise<{ isAdmin: boolean }> {
    return this.request(`/auth/is-admin?address=${address}`)
  }

  async verifySignature(message: string, signature: string): Promise<{ token: string; user: any }> {
    return this.request('/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ message, signature }),
    })
  }

  async getMe(): Promise<any> {
    return this.request('/auth/me')
  }

  // Public Bridge endpoints
  async getPublicBridgeConfig(): Promise<PublicBridgeConfig> {
    return this.request('/bridge/config')
  }

  // User Bridge endpoints
  async createBridgeRequest(sourceTxHash: string, fromAddress: string, toAddress?: string): Promise<any> {
    return this.request('/bridge/request', {
      method: 'POST',
      body: JSON.stringify({
        sourceTxHash,
        fromAddress,
        toAddress: toAddress || fromAddress, // Use fromAddress as default destination
        direction: 'UNI2_TO_BSC',
      }),
    })
  }

  async getUserBridgeRequests(params?: { status?: string; limit?: number; offset?: number }): Promise<any> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.append('status', params.status)
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.offset) searchParams.append('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request(`/bridge/requests${query ? `?${query}` : ''}`)
  }

  async syncCancellation(lockId: string): Promise<any> {
    return this.request(`/bridge/request/cancel-by-lock/${lockId}`, {
      method: 'POST',
    })
  }

  async getRejectionReason(lockId: string, address: string): Promise<RejectionReasonResponse> {
    return this.request(`/bridge/rejection-reason/${lockId}?address=${address}`)
  }

  async getSourceTxHash(lockId: string, address: string): Promise<SourceTxHashResponse> {
    return this.request(`/bridge/source-tx/${lockId}?address=${address}`)
  }

  // Admin Config endpoints
  async getAdminConfig(): Promise<BridgeConfig> {
    return this.request('/admin/bridge/config')
  }

  async updateAdminConfig(data: UpdateConfigDto): Promise<BridgeConfig> {
    return this.request('/admin/bridge/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Admin Audit endpoints
  async getAuditLogs(params?: AuditLogsQuery): Promise<AuditLogsResponse> {
    const searchParams = new URLSearchParams()
    if (params?.action) searchParams.append('action', params.action)
    if (params?.requestId) searchParams.append('requestId', params.requestId)
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.offset) searchParams.append('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request(`/admin/bridge/audit-log${query ? `?${query}` : ''}`)
  }

  // Admin Stats endpoints
  async getAdminStats(): Promise<any> {
    return this.request('/admin/bridge/stats')
  }

  // Admin Queue endpoints
  async getAdminQueue(params?: QueueQuery): Promise<any> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.append('status', params.status)
    if (params?.search) searchParams.append('search', params.search)
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.offset) searchParams.append('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request(`/admin/bridge/queue${query ? `?${query}` : ''}`)
  }

  async approveRequest(id: string): Promise<any> {
    return this.request(`/admin/bridge/request/${id}/approve`, { method: 'POST' })
  }

  async rejectRequest(id: string, reason: string, showReasonToUser: boolean = false): Promise<any> {
    return this.request(`/admin/bridge/request/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason, showReasonToUser }),
    })
  }

  async confirmRelease(id: string, destTxHash: string): Promise<any> {
    return this.request(`/admin/bridge/request/${id}/confirm-release`, {
      method: 'POST',
      body: JSON.stringify({ destTxHash }),
    })
  }

  async manualAddBridgeRequest(sourceTxHash: string): Promise<any> {
    return this.request('/admin/bridge/request/manual-add', {
      method: 'POST',
      body: JSON.stringify({ sourceTxHash }),
    })
  }

  async syncByLockId(lockId: number): Promise<SyncResult> {
    return this.request(`/admin/bridge/sync/lock/${lockId}`, {
      method: 'POST',
    })
  }

  async syncAllPendingLocks(filterAddress?: string): Promise<SyncAllResult> {
    return this.request('/admin/bridge/sync/all-pending', {
      method: 'POST',
      body: JSON.stringify({ filterAddress }),
    })
  }

  // Admin Bulk Actions
  async bulkApprove(requestIds: string[]): Promise<BulkActionResult> {
    return this.request('/admin/bridge/bulk/approve', {
      method: 'POST',
      body: JSON.stringify({ requestIds }),
    })
  }

  async bulkReject(requestIds: string[], reason: string): Promise<BulkActionResult> {
    return this.request('/admin/bridge/bulk/reject', {
      method: 'POST',
      body: JSON.stringify({ requestIds, reason }),
    })
  }

  // Admin Email Config
  async updateEmailConfig(data: UpdateEmailConfigDto): Promise<BridgeConfig> {
    return this.request('/admin/bridge/config/email', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  // Admin Payment endpoints
  async getPaymentBalances(): Promise<PaymentBalancesResponse> {
    return this.request('/admin/bridge/payment/balances')
  }

  async getPaymentStatus(): Promise<PaymentStatusResponse> {
    return this.request('/admin/bridge/payment/status')
  }

  async checkPayment(requestId: string): Promise<PaymentCheckResponse> {
    return this.request(`/admin/bridge/payment/check/${requestId}`)
  }

  async executePayment(requestId: string): Promise<PaymentResult> {
    return this.request('/admin/bridge/payment/execute', {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    })
  }

  async sendUsdtOnly(requestId: string): Promise<{ success: boolean; bscTxHash: string; message: string }> {
    return this.request('/admin/bridge/payment/send-usdt', {
      method: 'POST',
      body: JSON.stringify({ requestId }),
    })
  }

  async completeOnContract(requestId: string, bscTxHash: string): Promise<{ success: boolean; uni2TxHash: string; bscTxHash: string }> {
    return this.request('/admin/bridge/payment/complete-contract', {
      method: 'POST',
      body: JSON.stringify({ requestId, bscTxHash }),
    })
  }

  // Verify BSC transaction
  async verifyBscTx(txHash: string): Promise<VerifyBscTxResponse> {
    return this.request(`/admin/bridge/payment/verify-bsc/${txHash}`)
  }

  // Schedule payment endpoints
  async schedulePayment(requestId: string, scheduledAt: string, notes?: string): Promise<SchedulePaymentResponse> {
    return this.request('/admin/bridge/payment/schedule', {
      method: 'POST',
      body: JSON.stringify({ requestId, scheduledAt, notes }),
    })
  }

  async cancelSchedule(requestId: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/admin/bridge/payment/schedule/${requestId}`, {
      method: 'DELETE',
    })
  }

  async getScheduledPayments(): Promise<{ scheduled: BridgeRequest[] }> {
    return this.request('/admin/bridge/payment/scheduled')
  }

  // Bulk payment endpoints
  async bulkPaymentCheck(requestIds: string[]): Promise<BulkPaymentCheckResponse> {
    return this.request('/admin/bridge/payment/bulk/check', {
      method: 'POST',
      body: JSON.stringify({ requestIds }),
    })
  }

  async bulkPaymentExecute(requestIds: string[]): Promise<BulkPaymentExecuteResponse> {
    return this.request('/admin/bridge/payment/bulk/execute', {
      method: 'POST',
      body: JSON.stringify({ requestIds }),
    })
  }

  // Admin Wallets endpoints
  async getWalletBalances(): Promise<{ wallets: any[]; contracts: any[] }> {
    return this.request('/admin/wallets/balances')
  }

  // Admin Holders endpoints
  async getHoldersSummary(): Promise<HoldersSummary[]> {
    return this.request('/admin/holders/summary')
  }

  async getHolders(tokenType: TokenType, params?: { limit?: number; offset?: number }): Promise<HoldersResponse> {
    const searchParams = new URLSearchParams()
    if (params?.limit) searchParams.append('limit', String(params.limit))
    if (params?.offset) searchParams.append('offset', String(params.offset))

    const query = searchParams.toString()
    return this.request(`/admin/holders/${tokenType}${query ? `?${query}` : ''}`)
  }

  async syncHolders(tokenType: TokenType): Promise<{ message: string }> {
    return this.request(`/admin/holders/${tokenType}/sync`, { method: 'POST' })
  }

  async syncHoldersFromApi(tokenType: TokenType): Promise<{ message: string }> {
    return this.request(`/admin/holders/${tokenType}/sync-api`, { method: 'POST' })
  }

  async syncAllHolders(): Promise<{ message: string }> {
    return this.request('/admin/holders/sync-all', { method: 'POST' })
  }

  // User details endpoint
  async getUserDetails(walletAddress: string): Promise<UserDetailsResponse> {
    return this.request(`/admin/bridge/user-details/${walletAddress}`)
  }

  // ==========================================
  // Wallet Alias endpoints
  // ==========================================

  async getWalletAlias(walletAddress: string): Promise<WalletAlias | null> {
    return this.request(`/admin/bridge/wallet-alias/${walletAddress}`)
  }

  async setWalletAlias(walletAddress: string, alias: string): Promise<WalletAlias> {
    return this.request('/admin/bridge/wallet-alias', {
      method: 'POST',
      body: JSON.stringify({ walletAddress, alias }),
    })
  }

  async deleteWalletAlias(walletAddress: string): Promise<{ success: boolean }> {
    return this.request(`/admin/bridge/wallet-alias/${walletAddress}/delete`, {
      method: 'POST',
    })
  }

  // ==========================================
  // Rejection Templates endpoints
  // ==========================================

  async getRejectionTemplates(includeInactive = false): Promise<RejectionTemplate[]> {
    const query = includeInactive ? '?includeInactive=true' : ''
    return this.request(`/admin/templates${query}`)
  }

  async getDefaultRejectionTemplate(): Promise<RejectionTemplate | null> {
    return this.request('/admin/templates/default')
  }

  async getRejectionTemplate(id: string): Promise<RejectionTemplate> {
    return this.request(`/admin/templates/${id}`)
  }

  async createRejectionTemplate(data: CreateRejectionTemplateDto): Promise<RejectionTemplate> {
    return this.request('/admin/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async updateRejectionTemplate(id: string, data: UpdateRejectionTemplateDto): Promise<RejectionTemplate> {
    return this.request(`/admin/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async deleteRejectionTemplate(id: string): Promise<{ success: boolean }> {
    return this.request(`/admin/templates/${id}`, {
      method: 'DELETE',
    })
  }

  async setDefaultRejectionTemplate(id: string): Promise<RejectionTemplate> {
    return this.request(`/admin/templates/${id}/default`, {
      method: 'PATCH',
    })
  }

  // ============================================================
  // P2P endpoints
  // ============================================================

  async getP2POrderbook(pair: string): Promise<P2POrderbookSnapshot> {
    return this.request(`/p2p/orderbook/${encodeURIComponent(pair)}`);
  }

  async getP2PDepth(pair: string): Promise<P2PDepth> {
    return this.request(`/p2p/orderbook/${encodeURIComponent(pair)}/depth`);
  }

  async getP2POrder(orderId: string): Promise<P2POrder | null> {
    return this.request(`/p2p/orders/${encodeURIComponent(orderId)}`);
  }

  async getMyP2POrders(address: string): Promise<P2POrder[]> {
    return this.request(`/p2p/orders/me?address=${encodeURIComponent(address)}`);
  }

  async getMyP2PTrades(address: string): Promise<P2PTrade[]> {
    return this.request(`/p2p/trades/me?address=${encodeURIComponent(address)}`);
  }

  async getP2PStats(pair: string = 'W2USDT_USDT'): Promise<P2PStats> {
    return this.request(`/p2p/stats?pair=${encodeURIComponent(pair)}`);
  }

  async createP2POrderCalldata(dto: CreateP2POrderDto): Promise<P2PCalldata> {
    return this.request('/p2p/orders', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  }

  async cancelP2POrderCalldata(orderId: string): Promise<P2PCalldata> {
    return this.request(`/p2p/orders/${encodeURIComponent(orderId)}/cancel`, {
      method: 'POST',
    });
  }

  async reportP2POrder(chain: 'UNI2' | 'BSC', orderId: string, txHash: string): Promise<{ status: string }> {
    return this.request(`/p2p/orders/report`, {
      method: 'POST',
      body: JSON.stringify({ chain, orderId, txHash }),
    });
  }

  async crossP2PExistingOrders(args: {
    myOrderId: string;
    targetOrderId: string;
    address: string;
    confirmLargeDelta?: boolean;
  }): Promise<CrossExistingResponse> {
    const { myOrderId, targetOrderId, address, confirmLargeDelta = false } = args;
    return this.request(
      `/p2p/orders/${encodeURIComponent(myOrderId)}/cross/${encodeURIComponent(targetOrderId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ address, confirmLargeDelta }),
      },
    );
  }

  // ===== P2P Admin (V2 contract config) =====

  async getP2PContractConfig(chain: 'BSC' | 'UNI2'): Promise<P2PContractConfig> {
    return this.request(`/p2p/admin/config/${chain}`);
  }

  async buildP2PAdminCalldata(
    endpoint: string,
    payload: Record<string, unknown>,
  ): Promise<P2PCalldata> {
    return this.request(`/p2p/admin/config/${endpoint}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async buildP2PEmergencyWithdraw(
    orderId: string,
    chain: 'BSC' | 'UNI2',
    alternateRecipient: string,
  ): Promise<P2PCalldata> {
    return this.request(`/p2p/admin/orders/${orderId}/emergency-withdraw-to`, {
      method: 'POST',
      body: JSON.stringify({ chain, alternateRecipient }),
    });
  }

  async getP2PRelayerBalances(): Promise<P2PRelayerBalanceResponse> {
    return this.request(`/p2p/admin/relayer-balance`);
  }

  async runP2PExpiration(): Promise<{ ok: true; attempted: number; skipped: boolean }> {
    return this.request(`/p2p/admin/expiration/run`, { method: 'POST' });
  }
}

// Types

export interface P2PContractConfig {
  chain: 'BSC' | 'UNI2'
  enabled: boolean
  address?: string
  relayer?: string
  treasury?: string
  token?: string
  paused?: boolean
  minOrderAmount?: string
  maxPremiumBps?: number
  maxExpirationSeconds?: number
  platformFeeBps?: number
  gasFeeFlat?: string
  takeMaxTtlSeconds?: number
  maxVolumePer24h?: string
  currentWindowVolume?: string
  currentWindowStart?: number
  availableWindowCapacity?: string
  feesAccrued?: string
}

export interface PublicBridgeConfig {
  feePercentage: number
  minAmount: string
  maxAmount: string
  minAmountFormatted: string
  maxAmountFormatted: string
  showQueuePosition: boolean
  isPaused: boolean
  pauseReason: string | null
  lockContractAddress: string
  /** Feature flag: when false, the P2P UI must be hidden and routes
   *  guarded. Backend also returns 404 from /p2p/* endpoints in that case. */
  p2pEnabled: boolean
  chains: {
    uni2: { chainId: number; rpc: string; explorer: string }
    bsc: { chainId: number; rpc: string; explorer: string }
  }
}

export interface BridgeConfig {
  id: string
  lockContractAddress: string
  treasuryWalletBsc: string | null
  feeWalletBsc: string | null
  bridgeReceiveWalletBsc: string | null
  feePercentage: number
  minBridgeAmount: string
  maxBridgeAmount: string
  largePaymentThreshold: string | null
  showQueuePosition: boolean
  processingMode: 'MANUAL' | 'AUTOMATIC' | 'HYBRID'
  isPaused: boolean
  pauseReason: string | null
  // Email config
  emailEnabled: boolean
  smtpHost: string | null
  smtpPort: number
  smtpSecure: boolean
  smtpUser: string | null
  smtpPass: string | null
  smtpFromName: string
  smtpFromEmail: string
}

export interface UpdateEmailConfigDto {
  emailEnabled?: boolean
  smtpHost?: string
  smtpPort?: number
  smtpSecure?: boolean
  smtpUser?: string
  smtpPass?: string
  smtpFromName?: string
  smtpFromEmail?: string
}

export interface BulkActionResult {
  success: boolean
  approved?: number
  rejected?: number
  failed: number
  details: {
    success: string[]
    failed: { id: string; error: string }[]
  }
}

export interface MongoUserData {
  name: string | null
  email: string | null
  username: string | null
  isVip: boolean
}

export interface BridgeRequest {
  id: string
  userId: string
  direction: 'UNI2_TO_BSC' | 'BSC_TO_UNI2'
  sourceTxHash: string
  destTxHash: string | null
  fromAddress: string
  toAddress: string
  amountIn: string
  amountOut: string
  formattedAmountIn: string
  formattedAmountOut: string
  feePercentage: number
  feeAmount: string
  status: 'PENDING' | 'APPROVED' | 'COMPLETED' | 'REJECTED' | 'CANCELLED' | 'SCHEDULED' | 'FAILED'
  queuePosition: number | null
  rejectionReason: string | null
  createdAt: string
  updatedAt: string
  mongoUserData: MongoUserData | null
}

export interface UpdateConfigDto {
  feePercentage?: number
  minBridgeAmount?: string
  maxBridgeAmount?: string
  largePaymentThreshold?: string | null
  showQueuePosition?: boolean
  processingMode?: 'MANUAL' | 'AUTOMATIC' | 'HYBRID'
  isPaused?: boolean
  pauseReason?: string
  p2pEnabled?: boolean
}

export interface AuditLog {
  id: string
  requestId: string | null
  action: string
  adminId: string | null
  details: Record<string, any> | null
  txHash: string | null
  chainId: number | null
  createdAt: string
}

export interface AuditLogsQuery {
  action?: string
  requestId?: string
  limit?: number
  offset?: number
}

export interface AuditLogsResponse {
  logs: AuditLog[]
  total: number
}

export interface QueueQuery {
  status?: string
  search?: string
  limit?: number
  offset?: number
}

export type TokenType = 'W2USDT' | 'UNI2_TOKEN' | 'UNI2_COIN'

export interface TokenHolderUserData {
  name: string | null
  username: string | null
  email: string | null
  isVip: boolean
}

export interface TokenHolder {
  id: string
  tokenType: TokenType
  walletAddress: string
  balance: string
  balanceFormatted: string
  lastUpdated: string
  createdAt: string
  userData: TokenHolderUserData | null
}

export interface HoldersSummary {
  tokenType: TokenType
  symbol: string
  configured: boolean
  totalHolders: number
  totalSupplyFormatted: string
  lastSyncAt: string | null
  isRunning?: boolean
}

export interface HoldersResponse {
  tokenType: TokenType
  symbol: string
  holders: TokenHolder[]
  total: number
  totalSupply: string
  totalSupplyFormatted: string
  lastSyncAt: string | null
  lastSyncBlock: number | null
  isRunning: boolean
}

// Payment interfaces
export interface WalletBalance {
  address: string
  usdt?: string
  usdtFormatted?: string
  bnb?: string
  bnbFormatted?: string
  native?: string
  nativeFormatted?: string
  hasEnoughGas: boolean
}

export interface PaymentBalancesResponse {
  configured: boolean
  message?: string
  balances?: {
    bsc: WalletBalance
    uni2: WalletBalance
  }
}

export interface PaymentStatusResponse {
  configured: boolean
  addresses: {
    bsc: string
    uni2: string
  } | null
}

export interface PaymentCheckResponse {
  canExecute: boolean
  reason?: string
  balances?: {
    bsc: WalletBalance
    uni2: WalletBalance
  }
  request?: {
    id: string
    lockId: number
    amountOut: string
    formattedAmountOut: string
    toAddress: string
    status: string
  }
}

export interface PaymentResult {
  success: boolean
  bscTxHash: string
  uni2TxHash: string
  amountSent: string
  recipient: string
}

// Verify BSC TX
export interface VerifyBscTxResponse {
  valid: boolean
  recipient?: string
  amount?: string
  amountFormatted?: string
  confirmed?: boolean
  error?: string
}

// Schedule payment
export interface SchedulePaymentResponse {
  success: boolean
  scheduledAt: string
  schedule?: {
    id: string
    requestId: string
    scheduledAt: string
    notes?: string
  }
}

// Bulk payment
export interface BulkPaymentCheckResponse {
  canExecute: boolean
  reason?: string
  totalUsdt: string
  totalUsdtFormatted: string
  totalBnbGas: string
  totalUni2Gas: string
  balances?: {
    bsc: WalletBalance
    uni2: WalletBalance
  }
  requests: Array<{
    id: string
    lockId: number
    amountOut: string
    formattedAmountOut: string
    toAddress: string
    status: string
  }>
}

export interface BulkPaymentExecuteResponse {
  success: boolean
  total: number
  completed: number
  failed: number
  results: Array<{
    requestId: string
    success: boolean
    bscTxHash?: string
    uni2TxHash?: string
    error?: string
  }>
}

// User Details interfaces
export interface UserPlan {
  id: string
  planLevel: number
  amount: string
  payedAmount: string
  isClosed: boolean
  isCorporate: boolean
  hasPassiveIncome: boolean
  createdAt: string
}

export interface UserBalances {
  balance_direct: string
  balance_commission: string
  balance_passive_income: string
  matrix_reward: string
  balance: string
  balance_special: string
}

export interface PlansSummary {
  totalPlans: number
  activePlans: number
  closedPlans: number
  corporatePlans: number
  passiveIncomePlans: number
  totalInvested: string
  totalCorporateInvested: string
}

export interface TokenBalance {
  balance: string
  balanceFormatted: string
  lastUpdated: string | null
}

export interface UserDetailsResponse {
  user: {
    id: string
    username: string | null
    email: string | null
    name: string | null
    isVip: boolean
  } | null
  plans: UserPlan[]
  balances: UserBalances
  plansSummary: PlansSummary
  tokens: {
    W2USDT: TokenBalance
    UNI2_TOKEN: TokenBalance
    UNI2_COIN: TokenBalance
  }
}

// Rejection Templates interfaces
export interface RejectionTemplate {
  id: string
  name: string
  content: string
  isDefault: boolean
  order: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateRejectionTemplateDto {
  name: string
  content: string
  isDefault?: boolean
  order?: number
}

export interface UpdateRejectionTemplateDto {
  name?: string
  content?: string
  isDefault?: boolean
  order?: number
  isActive?: boolean
}

// Sync interfaces
export interface SyncResult {
  success: boolean
  message: string
  request?: BridgeRequest
  lockInfo?: any
}

export interface SyncAllResult {
  success: boolean
  totalLocks: number
  synced: number
  skipped: number
  errors: number
  results: Array<{ lockId: number; success: boolean; message: string }>
}

// Rejection reason response
export interface RejectionReasonResponse {
  hasReason: boolean
  reason: string | null
}

// Source tx hash response
export interface SourceTxHashResponse {
  found: boolean
  sourceTxHash: string | null
}

// Wallet alias
export interface WalletAlias {
  id: string
  walletAddress: string
  alias: string
  createdBy: string | null
  createdAt: string
  updatedAt: string
}

export const api = new ApiClient()
/** Alias kept for newer modules that import `apiClient` by convention. */
export const apiClient = api
